import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .tss * { box-sizing:border-box; }
  .tss { font-family:'Sora',sans-serif; color:#1a1d2e; animation:tssFade .4s ease both; }
  @keyframes tssFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .tss-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .tss-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .tss-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); transform:translateY(-1px); }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }

  .info-box { background:#eef2ff; border:1px solid #c7d2fe; border-radius:12px; padding:12px 16px; margin-bottom:1.4rem; font-size:.78rem; color:#3730a3; line-height:1.6; }
  .info-box strong { font-weight:700; }

  .tss-filters { display:flex; gap:14px; flex-wrap:wrap; margin-bottom:1.4rem; align-items:flex-end; }
  .tss-filters .form-field { min-width:220px; flex:1; max-width:340px; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input, .form-select { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus, .form-select:focus { border-color:#3b6ef8; background:white; }

  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .split-table { width:100%; border-collapse:collapse; }
  .split-table thead { background:#fafbff; border-bottom:2px solid #edf0f9; }
  .split-table th { padding:10px 12px; text-align:left; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .split-table td { padding:10px 12px; font-size:.82rem; vertical-align:middle; }
  .split-table tbody tr { border-bottom:1px solid #f3f5fb; }
  .split-table tbody tr:last-child { border-bottom:none; }
  .split-input { width:110px; font-family:'JetBrains Mono',monospace; font-size:.82rem; padding:7px 9px; border:1.5px solid #e0e4f0; border-radius:7px; background:#f8f9fc; outline:none; }
  .split-input:focus { border-color:#3b6ef8; background:white; }
  .split-input-narrow { width:76px; }
  .subj-name { font-weight:600; color:#0f1123; }
  .subj-derived { font-size:.7rem; color:#9094a8; margin-top:2px; }

  .tss-empty { text-align:center; padding:2.5rem 2rem; color:#9094a8; font-size:.82rem; }
  .tss-loading { display:flex; align-items:center; justify-content:center; height:160px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  @media (max-width:768px) {
    .tss-title { font-size:1.1rem; }
    .tss-filters .form-field { max-width:none; }
    .panel-head { flex-direction:column; align-items:flex-start; }
  }
`;

const idOf = (o) => o?._id ?? o?.id;
const fmtNum = (n) => { const v = Number(n); return isFinite(v) ? String(Math.round(v * 100) / 100) : '—'; };

const TermSubjectSplit = () => {
  const [terms,      setTerms]      = useState([]);
  const [classes,    setClasses]    = useState([]);
  const [termId,     setTermId]     = useState('');
  const [classId,    setClassId]    = useState('');
  const [section,    setSection]    = useState('');    // '' = All Sections
  const [sections,   setSections]   = useState([]);
  const [componentNames, setComponentNames] = useState([]);
  const [loadingOpts, setLoadingOpts] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [meta,        setMeta]        = useState(null); // { termName, className }
  const [rows,        setRows]        = useState([]);   // [{ subject, paperMarks, paperPercent, defaultPaperMarks, defaultPaperPercent, components }]

  useEffect(() => {
    (async () => {
      try {
        const [t, c] = await Promise.all([
          api.get('/admin/term-periods'),
          api.get('/admin/classes'),
        ]);
        setTerms((t.data || []).filter(x => x.active !== false));
        setClasses(Array.isArray(c.data) ? c.data : (c.data?.classes || []));
      } catch { toast.error('Failed to load terms/classes'); }
      finally { setLoadingOpts(false); }
    })();
  }, []);

  const fetchSplits = useCallback(async () => {
    if (!termId || !classId) { setRows([]); setMeta(null); setSections([]); setComponentNames([]); return; }
    try {
      setLoadingRows(true);
      const { data } = await api.get('/admin/term-subject-splits', { params: { termPeriodId: termId, classId, section } });
      setMeta({ termName: data?.termName, className: data?.className });
      setSections(Array.isArray(data?.sections) ? data.sections : []);
      setComponentNames(Array.isArray(data?.componentNames) ? data.componentNames : []);
      setRows((data?.subjects || []).map(s => ({
        subject: s.subject,
        paperMarks: s.paperMarks != null ? String(s.paperMarks) : '',
        paperPercent: s.paperPercent != null ? String(s.paperPercent) : '',
        defaultPaperMarks: s.defaultPaperMarks,
        defaultPaperPercent: s.defaultPaperPercent,
        components: (s.components || []).map(c => ({
          name: c.name,
          percent: c.percent != null ? String(c.percent) : '',
          defaultPercent: c.defaultPercent,
        })),
      })));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load subject splits');
      setRows([]); setMeta(null);
    } finally { setLoadingRows(false); }
  }, [termId, classId, section]);

  useEffect(() => { fetchSplits(); }, [fetchSplits]);

  const updateRow = (idx, field, value) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const updateComponent = (idx, componentName, value) => {
    setRows(prev => {
      const next = [...prev];
      const row = next[idx];
      const components = (row.components || []).map(c => c.name === componentName ? { ...c, percent: value } : c);
      next[idx] = { ...row, components };
      return next;
    });
  };

  const handleSave = async () => {
    const items = [];
    for (const r of rows) {
      const marksNum = r.paperMarks === '' ? 0 : Number(r.paperMarks);
      if (isNaN(marksNum) || marksNum < 0) { toast.error(`${r.subject}: paper marks must be a positive number`); return; }
      const pctNum = r.paperPercent === '' ? 0 : Number(r.paperPercent);
      if (isNaN(pctNum) || pctNum < 0 || pctNum > 100) { toast.error(`${r.subject}: paper share must be between 0 and 100`); return; }
      const components = (r.components || []).map(c => {
        const v = c.percent === '' || c.percent == null ? NaN : Number(c.percent);
        return { name: c.name, percent: (!isNaN(v) && v > 0) ? v : null };
      });
      items.push({
        subject: r.subject,
        paperMarks: marksNum > 0 ? marksNum : null,
        paperPercent: pctNum > 0 ? pctNum : null,
        components,
      });
    }
    try {
      setSaving(true);
      const { data } = await api.put('/admin/term-subject-splits', { termPeriodId: termId, classId, section, items });
      toast.success(`Saved ${data?.saved ?? items.length} subject split${(data?.saved ?? items.length) === 1 ? '' : 's'}`);
      fetchSplits();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="tss">
      <style>{style}</style>

      <div className="tss-header">
        <div>
          <div className="tss-title">Subject Split</div>
          <div className="tss-sub">Set each subject's term paper share, per term and class.</div>
        </div>
      </div>

      <div className="info-box">
        <strong>How this works:</strong> Set how much of each subject's 100% the term paper is worth.
        Whatever's left is shared automatically by that subject's assessments — you don't set that part.
      </div>

      {loadingOpts ? (
        <div className="tss-loading"><div className="spinner" />Loading…</div>
      ) : (
        <>
          <div className="tss-filters">
            <div className="form-field">
              <label className="form-label">Term</label>
              <select className="form-select" value={termId} onChange={e => setTermId(e.target.value)}>
                <option value="">Select term…</option>
                {terms.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Class</label>
              <select className="form-select" value={classId} onChange={e => { setClassId(e.target.value); setSection(''); }}>
                <option value="">Select class…</option>
                {classes.map(c => <option key={idOf(c)} value={idOf(c)}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Section</label>
              <select className="form-select" value={section} onChange={e => setSection(e.target.value)}>
                <option value="">All Sections</option>
                {sections.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="tss-sub" style={{ marginTop: '-10px', marginBottom: '1.4rem' }}>
            Leave on "All Sections" unless one section needs a different split — everything defaults to All Sections.
          </div>

          {!termId || !classId ? (
            <div className="panel"><div className="tss-empty">Select a term and a class to view and set its subject splits.</div></div>
          ) : loadingRows ? (
            <div className="tss-loading"><div className="spinner" />Loading…</div>
          ) : (
            <div className="panel">
              <div className="panel-head">
                <div className="panel-title">{meta?.termName || ''} · {meta?.className || ''}</div>
                <button className="btn btn-blue" onClick={handleSave} disabled={saving || rows.length === 0}>
                  <CheckCircleIcon />{saving ? 'Saving…' : 'Save'}
                </button>
              </div>
              {rows.length === 0 ? (
                <div className="tss-empty">No subjects found for this class.</div>
              ) : (
                <div style={{overflowX:'auto'}}>
                  <table className="split-table">
                    <thead>
                      <tr>
                        <th>Subject</th>
                        <th>Paper marked out of (marks)</th>
                        <th>Paper share in the term (%)</th>
                        {componentNames.map(name => <th key={name}>{name} %</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const hasDefault = section !== '' && r.defaultPaperPercent != null;
                        const effectivePct = Number(r.paperPercent) || (hasDefault ? Number(r.defaultPaperPercent) : 0);
                        const marksPlaceholder = (section !== '' && r.defaultPaperMarks != null)
                          ? `${fmtNum(r.defaultPaperMarks)} (default)` : 'e.g. 100';
                        const pctPlaceholder = (section !== '' && r.defaultPaperPercent != null)
                          ? `${fmtNum(r.defaultPaperPercent)} (default)` : 'e.g. 60';
                        const baseDerivedText = effectivePct > 0
                          ? `Assessments share the remaining ${fmtNum(Math.max(0, 100 - effectivePct))}%`
                          : 'No paper — assessments share 100%';
                        const explicitComps = (r.components || []).filter(c => c.percent !== '' && c.percent != null && Number(c.percent) > 0);
                        const derivedText = explicitComps.length > 0
                          ? `${baseDerivedText} · ${explicitComps.map(c => `${c.name} ${fmtNum(c.percent)}%`).join(' · ')}`
                          : baseDerivedText;
                        return (
                          <tr key={r.subject}>
                            <td>
                              <div className="subj-name">{r.subject}</div>
                              <div className="subj-derived">{derivedText}</div>
                            </td>
                            <td>
                              <input type="number" className="split-input" value={r.paperMarks} min="0" step="any"
                                placeholder={marksPlaceholder} onChange={e => updateRow(i, 'paperMarks', e.target.value)} />
                            </td>
                            <td>
                              <input type="number" className="split-input" value={r.paperPercent} min="0" max="100" step="any"
                                placeholder={pctPlaceholder} onChange={e => updateRow(i, 'paperPercent', e.target.value)} />
                            </td>
                            {componentNames.map(name => {
                              const comp = (r.components || []).find(c => c.name === name) || { percent: '', defaultPercent: null };
                              const compPlaceholder = (section !== '' && comp.defaultPercent != null)
                                ? `${fmtNum(comp.defaultPercent)} (default)` : 'e.g. 20';
                              return (
                                <td key={name}>
                                  <input type="number" className="split-input split-input-narrow" value={comp.percent} min="0" max="100" step="any"
                                    placeholder={compPlaceholder} onChange={e => updateComponent(i, name, e.target.value)} />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TermSubjectSplit;
